import { ReactNode, useMemo } from 'react';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  split,
  ApolloLink,
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAntifraud } from './AntifraudProvider';
import { cache } from '../state/cache';

type Props = { children: ReactNode };

export const ApiProvider = ({ children }: Props) => {
  const { apiUrl, token, wsUrl, tenant } = useAntifraud();

  const client = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const httpLink = new HttpLink({
      uri: apiUrl + '/graphql',
      fetch: (...args) => fetch(...args),
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Tenant-Id': tenant?.id as string,
      },
    });

    let splitLink;

    if (wsUrl && wsUrl !== '' && token) {
      splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        new WebSocketLink({
          uri: `${wsUrl}graphql`,
          options: {
            reconnect: true,
            lazy: true,
            inactivityTimeout: 60000,
            connectionParams: {
              headers: {
                authorization: `Bearer ${token}`,
              },
              token,
            },
          },
        }),
        httpLink,
      );
    } else {
      splitLink = httpLink;
    }

    const link = ApolloLink.from([splitLink || httpLink]);

    return new ApolloClient({
      link,
      cache,
    });
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
